import { useEffect, useState } from "react";
import { ShieldCheck, UserPlus, X, Pencil, Trash2 } from "lucide-react";
import { Field, TextInput, SelectInput } from "@/components/brand/Field";
import { api, formatApiError } from "@/lib/api";

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "member" });
  const [editUser, setEditUser] = useState(null);
  const [ef, setEf] = useState({ name: "", email: "", role: "member", password: "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = () => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch((err) => setError(formatApiError(err.response?.data?.detail)));
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    try {
      await api.post("/admin/users", f);
      setShowForm(false);
      setF({ name: "", email: "", password: "", role: "member" });
      setNotice("User created.");
      load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEf({ name: u.name, email: u.email, role: u.role, password: "" });
    setError("");
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    try {
      const payload = { name: ef.name, email: ef.email, role: ef.role };
      if (ef.password) payload.password = ef.password;
      await api.patch(`/admin/users/${editUser.id}`, payload);
      setEditUser(null);
      setNotice("User updated.");
      load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name || u.email}? They will no longer be able to log in.`)) return;
    setError("");
    setNotice("");
    try {
      await api.delete(`/admin/users/${u.id}`);
      setNotice("User removed (deactivated).");
      load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-users">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="font-display text-2xl font-bold text-brand-snow sm:text-3xl">Admin users</h1>
          <p className="text-sm text-brand-mist">
            Admins manage everything. Editors can manage content — articles, pages, FAQs and portfolio — but never enquiries, settings, credentials or users.
          </p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="user-new" className="inline-flex items-center gap-2 rounded-full bg-brand-emerald px-5 py-2.5 font-display text-sm font-semibold text-brand-black hover:bg-brand-snow">
          <UserPlus size={15} /> Add user
        </button>
      </div>
      {notice && <p className="flex items-center gap-2 text-sm text-brand-emerald" data-testid="users-notice"><ShieldCheck size={15} /> {notice}</p>}
      {error && <p role="alert" className="text-sm text-red-400" data-testid="users-error">{error}</p>}

      <div className="surface-card divide-y divide-brand-mist/10" data-testid="users-list">
        {users.map((u) => (
          <div key={u.id} className="flex items-center justify-between gap-4 px-6 py-4" data-testid={`user-row-${u.email}`}>
            <div>
              <p className="text-sm font-medium text-brand-snow">{u.name} {me?.id === u.id && <span className="text-xs text-brand-mist">(you)</span>}</p>
              <p className="text-xs text-brand-mist">{u.email}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`rounded-full border px-3 py-1 text-[11px] font-medium ${u.role === "admin" ? "border-brand-emerald/40 text-brand-emerald" : u.role === "member" ? "border-brand-mist/40 text-brand-mist" : "border-brand-azure/40 text-brand-azure"}`} data-testid={`user-role-${u.email}`}>
                {u.role}
              </span>
              {u.active === false ? (
                <span className="text-xs text-brand-mist">deactivated</span>
              ) : (
                <>
                  <button onClick={() => openEdit(u)} aria-label="Edit user" data-testid={`user-edit-${u.email}`} className="rounded-md p-1.5 text-brand-mist transition-colors hover:text-brand-emerald">
                    <Pencil size={15} />
                  </button>
                  {me?.id !== u.id && (
                    <button onClick={() => remove(u)} aria-label="Remove user" data-testid={`user-delete-${u.email}`} className="rounded-md p-1.5 text-brand-mist transition-colors hover:text-red-400">
                      <Trash2 size={15} />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex justify-center bg-brand-black/70 backdrop-blur-sm sm:p-6" onClick={() => setShowForm(false)} data-testid="user-form-modal">
          <form onSubmit={add} className="h-full w-full max-w-md space-y-5 overflow-y-auto border border-brand-mist/15 bg-brand-charcoal p-6 sm:h-auto sm:rounded-2xl sm:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-brand-snow">Add admin user</h2>
              <button type="button" onClick={() => setShowForm(false)} aria-label="Close" className="rounded-md p-2 text-brand-mist hover:text-brand-snow"><X size={18} /></button>
            </div>
            <Field label="Name" htmlFor="nu-name" required><TextInput id="nu-name" testid="nu-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></Field>
            <Field label="Email" htmlFor="nu-email" required><TextInput id="nu-email" type="email" testid="nu-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required /></Field>
            <Field label="Temporary password" htmlFor="nu-password" required hint="Minimum 10 characters. Ask the user to change it after first sign-in.">
              <TextInput id="nu-password" type="password" testid="nu-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} minLength={10} required />
            </Field>
            <Field label="Role" htmlFor="nu-role">
              <SelectInput id="nu-role" testid="nu-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
                <option value="member">Member — projects only</option>
                <option value="sales">Sales — leads only</option>
                <option value="editor">Editor — content + projects</option>
                <option value="admin">Admin — full access</option>
              </SelectInput>
            </Field>
            <button type="submit" data-testid="nu-save" className="rounded-full bg-brand-emerald px-6 py-3 font-display text-sm font-semibold text-brand-black hover:bg-brand-snow">Create user</button>
          </form>
        </div>
      )}

      {editUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-black/70 p-4 backdrop-blur-sm" onClick={() => setEditUser(null)} data-testid="user-edit-modal">
          <form onSubmit={saveEdit} className="surface-card w-full max-w-md space-y-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-brand-snow">Edit user</h2>
              <button type="button" onClick={() => setEditUser(null)} aria-label="Close" className="text-brand-mist hover:text-brand-snow"><X size={18} /></button>
            </div>
            <Field label="Name"><TextInput required value={ef.name} onChange={(e) => setEf({ ...ef, name: e.target.value })} data-testid="edit-name" /></Field>
            <Field label="Email"><TextInput required type="email" value={ef.email} onChange={(e) => setEf({ ...ef, email: e.target.value })} data-testid="edit-email" /></Field>
            <Field label="Role">
              <SelectInput value={ef.role} onChange={(e) => setEf({ ...ef, role: e.target.value })} data-testid="edit-role">
                <option value="member">Member — projects only</option>
                <option value="sales">Sales — leads only</option>
                <option value="editor">Editor — content + projects</option>
                <option value="admin">Admin — full access</option>
              </SelectInput>
            </Field>
            <Field label="New password (leave blank to keep current)"><TextInput type="password" value={ef.password} onChange={(e) => setEf({ ...ef, password: e.target.value })} data-testid="edit-password" /></Field>
            {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setEditUser(null)} className="rounded-full border border-brand-mist/30 px-4 py-2 text-sm text-brand-mist">Cancel</button>
              <button type="submit" data-testid="edit-save" className="rounded-full bg-brand-emerald px-5 py-2 font-display text-sm font-semibold text-brand-black">Save changes</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
